import { ApiConfig } from './api';
import { fetchWrapper, objectToSearchParams } from './utils/http-utils';
import { moment } from '../libs';
import { ApiTypesWithConfig, batch, organization } from '../types';
import { Message, Thread } from '../types/ai-assistant';
import { GeneralAnalysis, ReportDefault, ResourceAnalysis } from '../types/analysis';
import { Portfolio as AppPortfolio } from '../types/app-portfolio';
import { PortfolioHoldings as AppPortfolioHoldings } from '../types/app-portfolio';
import { AuditLog } from '../types/audit-logs';
import { Backtest } from '../types/backtest';
import { BacktestSet } from '../types/backtest-set';
import { Basket } from '../types/basket';
import { Submission } from '../types/batch';
import { ResourceDraft, ResourcePreference } from '../types/common';
import { Country } from '../types/country';
import { Currency } from '../types/currency';
import { DataSource } from '../types/data-source';
import { FixedCost } from '../types/fixed-costs';
import { HelpItem } from '../types/help-item';
import { Job } from '../types/job';
import { Label } from '../types/label';
import { Log as PerformanceLog } from '../types/performance-logs';
import { Pipeline } from '../types/pipeline';
import { Portfolio } from '../types/portfolio';
import { PortfolioHoldings } from '../types/portfolio-holdings';
import { Rebalance } from '../types/rebalance';
import { RiskModel } from '../types/risk-model';
import { Signal } from '../types/signal';
import { Dataset } from '../types/signal';
import { ExplorationPreviewRecord } from '../types/signal';
import { SignalSet } from '../types/signal-set';
import { Strategy } from '../types/strategy';
import { TimeSeries } from '../types/time-series';
import { Universe } from '../types/universe';
import { User } from '../types/user';
import { UserPreference } from '../types/user-preference';
import { Workspace } from '../types/workspace';

function buildApis<T extends ResourceDraft>(resource: string): ApiTypesWithConfig<T> {
  return {
    get:
      (config: ApiConfig) =>
      (id, query = {}) => {
        const searchParams = objectToSearchParams({ ...(query ?? {}), allow_revoked: true });
        const url = `${config.endpoint}${resource}/${id}${searchParams ? `?${searchParams}` : ''}`;
        return fetchWrapper(
          url,
          {
            method: 'GET',
          },
          config
        );
      },
    update:
      (config: ApiConfig) =>
      (id, data, setDefUpdatedAt = false) => {
        const payload = setDefUpdatedAt ? { ...data, definition_updated_at: `${moment().valueOf()}` } : { ...data };

        return fetchWrapper(
          `${config.endpoint}${resource}/${id}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          },
          config
        );
      },
    create: (config: ApiConfig) => (data) =>
      fetchWrapper(
        `${config.endpoint}${resource}`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        },
        config
      ),
    remove: (config: ApiConfig) => (id) =>
      fetchWrapper(
        `${config.endpoint}${resource}/${id}`,
        {
          method: 'DELETE',
        },
        config
      ),
    find: (config: ApiConfig) => (query) => {
      const searchParams = objectToSearchParams({ ...(query ?? {}), allow_revoked: true });
      const url = `${config.endpoint}${resource}${searchParams ? `?${searchParams}` : ''}`;

      return fetchWrapper(
        url,
        {
          method: 'GET',
        },
        config
      );
    },
    search: (config: ApiConfig) => (data) =>
      fetchWrapper(
        `${config.endpoint}${resource}/search`,
        {
          method: 'POST',
          body: JSON.stringify({ ...(data ?? {}), allow_revoked: true }),
        },
        config
      ),
    listAdd: (config: ApiConfig) => (id: number, column: string, values: any) =>
      fetchWrapper(
        `${config.endpoint}${resource}/${id}/${column}`,
        {
          method: 'POST',
          body: JSON.stringify({ values }),
        },
        config
      ),
    listRemove: (config: ApiConfig) => (id: number, column: string, values: any) =>
      fetchWrapper(
        `${config.endpoint}${resource}/${id}/${column}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ values }),
        },
        config
      ),
    increment: (config: ApiConfig) => (id: number, incrementRecord: Record<string, any>) =>
      fetchWrapper(
        `${config.endpoint}${resource}/increment/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ update: {}, increment: incrementRecord }),
        },
        config
      ),
  };
}

const apis = {
  audit_logs: buildApis<AuditLog>('audit_logs'),
  countries: buildApis<Country>('countries'),
  currencies: buildApis<Currency>('currencies'),
  exchanges: buildApis<Record<string, any>>('exchanges'),
  resource_preferences: buildApis<ResourcePreference>('resource_preferences'),

  assistant_messages: buildApis<Message>('assistant_messages'),
  assistant_threads: buildApis<Thread>('assistant_threads'),
  backtest_sets: buildApis<BacktestSet>('backtest_sets'),
  backtests: buildApis<Backtest>('backtests'),
  baskets: buildApis<Basket>('baskets'),
  data_sources: buildApis<DataSource>('data_sources'),
  fixed_costs: buildApis<FixedCost>('fixed_costs'),
  performance_logs: buildApis<PerformanceLog>('ui_performance_logs'),
  pipelines: buildApis<Pipeline>('pipelines'),
  portfolio_holdings: buildApis<PortfolioHoldings>('portfolio_holdings'),
  portfolios: buildApis<Portfolio>('portfolios'),
  rebalances: buildApis<Rebalance>('rebalances'),
  risk_models: buildApis<RiskModel>('risk_models'),
  signal_datasets: buildApis<Dataset>('signal_datasets'),
  signal_exploration_previews: buildApis<ExplorationPreviewRecord>('signal_exploration_previews'),
  signal_sets: buildApis<SignalSet>('signal_sets'),
  signals: buildApis<Signal>('signals'),
  strategies: buildApis<Strategy>('strategies'),
  time_series: buildApis<TimeSeries>('time_series'),
  universes: buildApis<Universe>('universes'),
  user_preferences: buildApis<UserPreference>('user_preferences'),
  app_portfolios: buildApis<AppPortfolio>('app_portfolios'),
  app_portfolio_holdings: buildApis<AppPortfolioHoldings>('app_portfolio_holdings'),
  resource_analyses: buildApis<ResourceAnalysis>('resource_analyses'),
  general_analyses: buildApis<GeneralAnalysis>('general_analyses'),
  data_source_column_metadatas: buildApis<Record<string, any>>('data_source_column_metadatas'),
  jobs: buildApis<Job>('jobs'),
  reports: buildApis<GeneralAnalysis>('reports'),
  report_defaults: buildApis<ReportDefault>('report_defaults'),
  submission_batches: buildApis<batch.Batch>('submission_batches'),
  submissions: buildApis<Submission | batch.Submission>('submissions'),
  data_loader_logs: buildApis<Submission>('data_loader_logs'),
  workspaces: buildApis<Workspace>('workspaces'),
  organizations: buildApis<organization.Organization>('organizations'),
  organization_features: buildApis<organization.OrganizationFeatures>('organization_features'),
  users: buildApis<User>('users'),
  labels: buildApis<Label>('labels'),
  help_items: buildApis<HelpItem>('help_items'),
};

export default apis;
